// THE APPEAL THIS TAB IS ON — the one identifier the client keeps, and the only thing it keeps across a reload
// (WEB-APPEAL-ID, 2026-09-29).
//
// THE SERVER MINTS IT. `v2ResolveProperty` `address` with no `appealId` creates the appeal and answers it in the
// snapshot (`functions/src/v2/resolve/address.ts`, step 6); naming an id the account does not have is refused NOT_FOUND,
// and the store refuses a client-chosen id outright (`functions/src/v2/store/ids.ts`, design 4.1). So a tab starts
// holding nothing, sends no id on its first resolve, and adopts the id the answer carries. Until WEB-APPEAL-ID the
// session was handed the FAKE world's fixed sample id even on the real transport, and Tyler's first "Check this address"
// on the trial site came back NOT_FOUND 404: the fake had hidden it by starting with that appeal already made.
//
// IT SURVIVES A RELOAD IN THIS TAB, AND NOTHING ELSE DOES. Buying leaves for Stripe with a full-page navigation in this
// tab (`createBrowserCheckout`), and Stripe sends the owner back to `/checkout/return`; without the id they would come
// back to a credit and no appeal to spend it on. `sessionStorage` is per tab and ends with the tab — the session's own
// scope, "which appeal this tab is looking at" — and it survives that leave: measured in the Chromium preview pane on
// 2026-09-29, `window.open(url, "_self", "noopener")` loaded a new document in the SAME tab and the id was still there
// (Safari and Firefox not measured). It holds the server-minted identifier ONLY: never the address, the unit
// or anything the server said. Every read and write is in try/catch, so storage that is off, full or refused leaves the
// tab working, and a reload then starts over at `/`. The id is only good for the same sign-in, and it gets one:
// `signInAnonymously` reuses the anonymous user Firebase already keeps in this browser after a reload.
//
// WHY NOT `v2Outcomes` `list`. It answers every appeal on the account, newest first, and nothing in it says which one
// THIS tab was on. Adopting the newest would glue a second tab, or a fresh visit after a finished appeal, to that appeal
// — and re-resolving one whose credit is spent is refused APPEAL_LOCKED. Which property an owner is paying for is not
// the client's to guess.
//
// A STALE ID IS DROPPED, AND ONLY ON THE SERVER'S WORD. An id the account no longer has (the account was deleted, or
// this browser is signed in as someone else) is refused NOT_FOUND with one of the server's "we couldn't find that appeal
// on your account" sentences, listed below by code. Those, and nothing else, drop it: `letter.fetch.noSuchGeneration` is
// also a NOT_FOUND, about a letter and not the appeal — a `/letter` or `/building` URL naming a generation this appeal
// does not have.

import type { V2Result } from "@contract";
import type { Transport } from "../transport/types";

/** Where a tab keeps the id. A seam, so a test can stand up a fresh session over the same storage — a reload. */
export interface AppealIdStore {
  read(): string | null;
  write(appealId: string): void;
  clear(): void;
}

/** The one key this package writes to browser storage. Its value is a server-minted appeal id and nothing else. */
export const APPEAL_ID_KEY = "proppeal.appealId";

/** The tab's `sessionStorage`. A storage that throws — on access, read, write or clear — is a tab with no memory. */
export function sessionAppealIdStore(storage: () => Storage = () => window.sessionStorage): AppealIdStore {
  return {
    read() {
      try {
        const value = storage().getItem(APPEAL_ID_KEY);
        return value === null || value === "" ? null : value;
      } catch {
        return null;
      }
    },
    write(appealId) {
      try {
        storage().setItem(APPEAL_ID_KEY, appealId);
      } catch {
        // The id is still held in memory; only a reload loses it, and then the owner starts at `/`.
      }
    },
    clear() {
      try {
        storage().removeItem(APPEAL_ID_KEY);
      } catch {
        // Nothing was stored that could outlive this, or nothing can be stored at all.
      }
    },
  };
}

/** A store that remembers nothing beyond itself: tests, and the shape a reload has when storage is unavailable. */
export function memoryAppealIdStore(initial: string | null = null): AppealIdStore {
  let value = initial;
  return {
    read: () => value,
    write: (appealId) => {
      value = appealId;
    },
    clear: () => {
      value = null;
    },
  };
}

/**
 * The server's sentences for "that appeal is not on your account", by code — each read at its source
 * (`functions/src/v2/…`, every `sendError(…, "NOT_FOUND", …)` whose appeal read or append found nothing). A NOT_FOUND
 * under any other code says nothing about the appeal and drops nothing.
 */
export const APPEAL_NOT_FOUND_COPY: readonly string[] = [
  "resolve.address.notFound",
  "resolve.record.notFound",
  "credits.spend.notFound",
  "comps.notFound",
  "value.notFound",
  "evaluate.notFound",
  "letter.notFound",
  "outcomes.record.notFound",
];

/** True when the server refused because the appeal named is not on this account. */
export function refusesAppeal(result: V2Result<unknown>): boolean {
  return !result.ok && result.kind === "v2Error" && result.error.error.code === "NOT_FOUND"
    && APPEAL_NOT_FOUND_COPY.includes(result.error.error.copy.code);
}

type AnyCall = (input?: unknown) => Promise<V2Result<unknown>>;

/**
 * The transport, with every call that NAMED an appeal watched for the server saying that appeal is gone. The call and
 * its answer are untouched; `onRefused` is told which id was refused, and the session decides whether it is the one held.
 */
export function watchAppealRefusals(transport: Transport, onRefused: (appealId: string) => void): Transport {
  const watch = (call: AnyCall): AnyCall => async (input) => {
    const result = await call(input);
    const named = (input as { appealId?: unknown } | undefined)?.appealId;
    if (typeof named === "string" && refusesAppeal(result)) onRefused(named);
    return result;
  };
  const watched: Record<string, unknown> = {};
  for (const [endpoint, value] of Object.entries(transport as unknown as Record<string, unknown>)) {
    watched[endpoint] = typeof value === "function"
      ? watch(value as AnyCall)
      : Object.fromEntries(
        Object.entries(value as Record<string, AnyCall>).map(([op, call]) => [op, watch(call)]),
      );
  }
  return watched as unknown as Transport;
}
